import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("span", {
  class: "spinner-border spinner-border-sm me-2",
  role: "status",
  "aria-hidden": "true"
}, null, -1)

import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';

type OptionItem = { value: any; text: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchSelect',
  props: {
    modelValue: {},
    options: {},
    placeholder: {},
    required: { type: Boolean },
    invalidMessage: {},
    wasValidated: { type: Boolean },
    disabled: { type: Boolean },
    loading: { type: Boolean }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit }) {

const props = __props;





const rootEl = ref<HTMLElement | null>(null);
const searchTerm = ref('');
const isOpen = ref(false);
const activeIndex = ref(-1);
const listId = `ac-${Math.random().toString(36).slice(2)}`;

const touched = ref(false);

// Validation
const isEmpty = (v: any) => v == null || v === '';
const showErrors = computed(() => props.wasValidated === true || touched.value);
const isInvalid = computed(() => !!props.required && showErrors.value && isEmpty(props.modelValue));
const inputState = computed<null | boolean>(() => (isInvalid.value ? false : null));
const invalidText = computed(() => props.invalidMessage || 'Campo requerido');

// Filtering
const filteredOptions = computed<OptionItem[]>(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return props.options || [];
  return (props.options || []).filter(o => o.text.toLowerCase().includes(term));
});

// Show the list whenever the control is open (even if loading or 0 options)
const showList = computed(() => isOpen.value && !props.disabled);

// Helpers
function isZeroish(v: any) {
  return v === 0 || v === '0' || (typeof v === 'string' && v.trim() === '0');
}

// Actions
function selectOption(option: OptionItem) {
  if (isZeroish(option.value)) {
    clear(true);
    return;
  }
  searchTerm.value = option.text;
  emit('update:modelValue', option.value);
  emit('change', option.value);
  close();
}

function onInput() {
  open();
  activeIndex.value = 0;
}

function open() {
  if (props.disabled) return;
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  activeIndex.value = -1;
}

function clear(markTouched = true) {
  searchTerm.value = '';
  emit('update:modelValue', null);
  emit('change', null);
  if (markTouched) touched.value = true;
  close();
}

function onBlur() {
  // Defer marking as touched to allow option selection to update the model first
  setTimeout(() => {
    touched.value = true;
  }, 0);
}

function move(step: number) {
  if (!showList.value) {
    open();
    activeIndex.value = 0;
    return;
  }
  const n = filteredOptions.value.length;
  if (n === 0) return;
  activeIndex.value = (activeIndex.value + step + n) % n;

  // keep active item in view
  requestAnimationFrame(() => {
    const list = document.getElementById(listId);
    const items = list?.querySelectorAll('.list-group-item');
    const el = items?.[activeIndex.value] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  });
}

function commitActive() {
  const option = filteredOptions.value[activeIndex.value];
  if (option) selectOption(option);
  else close();
}

// sync text when external model changes
watch(
  () => props.modelValue,
  (newVal) => {
    if (isZeroish(newVal)) {
      clear(false);
      return;
    }
    if (newVal == null || newVal === '') {
      searchTerm.value = '';
      return;
    }
    const selected = (props.options || []).find(o => o.value === newVal);
    if (selected) searchTerm.value = selected.text;
  },
  { immediate: true }
);

// If options arrive while open (e.g., after async fetch), keep the list visible
watch(
  () => props.options,
  () => {
    if (isOpen.value && !props.disabled) {
      // no-op: showList already depends on isOpen; keeping here for clarity
    }
  }
);

// click-outside to close
function onDocClick(e: MouseEvent) {
  if (!rootEl.value) return;
  if (!rootEl.value.contains(e.target as Node)) close();
}
onMounted(() => document.addEventListener('mousedown', onDocClick));
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick));

return (_ctx: any,_cache: any) => {
  const _component_b_form_input = _resolveComponent("b-form-input")!
  const _component_b_list_group_item = _resolveComponent("b-list-group-item")!
  const _component_b_list_group = _resolveComponent("b-list-group")!
  const _component_b_form_invalid_feedback = _resolveComponent("b-form-invalid-feedback")!

  return (_openBlock(), _createElementBlock("div", {
    class: "position-relative",
    ref_key: "rootEl",
    ref: rootEl
  }, [
    _createVNode(_component_b_form_input, {
      modelValue: searchTerm.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
      placeholder: _ctx.placeholder,
      class: "mb-0 shadow-none fw-semibold rounded-0 pe-5",
      autocomplete: "off",
      role: "combobox",
      "aria-expanded": showList.value ? 'true' : 'false',
      "aria-controls": listId,
      onFocus: open,
      onClick: open,
      onInput: onInput,
      onKeydown: [
        _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (move(1)), ["prevent"]), ["down"])),
        _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (move(-1)), ["prevent"]), ["up"])),
        _withKeys(_withModifiers(commitActive, ["prevent"]), ["enter"]),
        _withKeys(_withModifiers(close, ["prevent"]), ["esc"])
      ],
      state: inputState.value,
      onBlur: onBlur,
      disabled: _ctx.disabled
    }, null, 8, ["modelValue", "placeholder", "aria-expanded", "onKeydown", "state", "disabled"]),
    (searchTerm.value && !_ctx.disabled)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "btn-close position-absolute top-50 end-0 translate-middle-y me-2",
          style: {"padding-right":"3rem","right":"4.5rem"},
          "aria-label": "Clear",
          onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop","prevent"])),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (clear()))
        }, null, 32))
      : _createCommentVNode("", true),
    _withDirectives(_createVNode(_component_b_list_group, {
      id: listId,
      class: "position-absolute w-100 bg-white border rounded shadow",
      style: {"z-index":"1051","max-height":"200px","overflow-y":"auto","top":"100%","left":"0"}
    }, {
      default: _withCtx(() => [
        (_ctx.loading)
          ? (_openBlock(), _createBlock(_component_b_list_group_item, {
              key: 0,
              disabled: ""
            }, {
              default: _withCtx(() => [
                _hoisted_1,
                _createTextVNode(" Cargando... ")
              ]),
              _: 1
            }))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option, idx) => {
                return (_openBlock(), _createBlock(_component_b_list_group_item, {
                  key: option.value,
                  button: "",
                  class: _normalizeClass({ active: idx === activeIndex.value }),
                  onMousemove: ($event: any) => (activeIndex.value = idx),
                  onMousedown: _withModifiers(($event: any) => (selectOption(option)), ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(option.text), 1)
                  ]),
                  _: 2
                }, 1032, ["class", "onMousemove", "onMousedown"]))
              }), 128)),
              (filteredOptions.value.length === 0)
                ? (_openBlock(), _createBlock(_component_b_list_group_item, {
                    key: 0,
                    disabled: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Sin resultados ")
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ], 64))
      ]),
      _: 1
    }, 512), [
      [_vShow, showList.value]
    ]),
    (isInvalid.value)
      ? (_openBlock(), _createBlock(_component_b_form_invalid_feedback, { key: 1 }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(invalidText.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 512))
}
}

})